/** `GET /coverage` from the browser's side (ADR-0166): the census with a waiting document marked.
 *
 * **What this read is for, and the one thing it must never be used for.** The same six states
 * `GET /sources` gives are on every window here, plus `waiting_documents`: documents that *may*
 * cover a window and are waiting for a person to review them. A screen reads this to say so
 * *beside* a window - never to change the window's state, its colour or any count. The state words
 * and every total still come from the census (`useCoverage`); this is the annotation only.
 *
 * **Keyed under the census's own coverage key**, so every mutation that invalidates the census
 * (declaring nil, retiring) refetches this too, and a stale "a document is waiting" never sits
 * beside a fresh state. Like the census it is refetched on mount.
 *
 * The role clamp is the server's: a role that may not read the document queue gets the same
 * response with no annotation, which is an honest empty - the screen is not told, and does not
 * guess, that a document exists.
 */

import { skipToken, useQuery } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";
import { requireShape } from "./shape";
import { sourceKeys } from "./sources";

export type CoverageRollup = components["schemas"]["CoverageRollupOut"];
export type WaitingDocument = components["schemas"]["WaitingDocumentOut"];

export function useCoverageRollup(
  periodStart: string | undefined,
  periodEnd: string | undefined,
  enabled = true,
  /** Something that changes when the answer can: the document card passes the file's state and
   * stated period, because the server can place a file on a window only once its period is known
   * (it is read from the document, after upload), and the window's own dates do not change. */
  stamp = "",
) {
  return useQuery({
    queryKey: [...sourceKeys.coverage(periodStart ?? "none", periodEnd ?? "none"), "rollup", stamp],
    enabled,
    // No dates, no request (`useCoverage` says why).
    queryFn:
      periodStart && periodEnd
        ? async (): Promise<CoverageRollup> => {
            const { data, error, response } = await api.GET("/api/v1/coverage", {
              params: { query: { period_start: periodStart, period_end: periodEnd } },
            });
            if (error || !data) {
              throw refusalFrom(
                response,
                error,
                "The documents waiting for review could not be read.",
              );
            }
            requireShape(Array.isArray(data.sites), "The documents waiting for review");
            return data;
          }
        : skipToken,
  });
}
